import React, { useEffect, useState } from 'react';
import useStore from '../../../store/useStore';
import toast from 'react-hot-toast';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import AddressCard from './AddressesSection/AddressCard';
import AddressFormModal from './AddressesSection/AddressFormModal';
import { EMPTY_FORM } from './AddressesSection/addressValidation';

const AddressesSection = () => {
  const {
    user_addresses,
    addresses_loading,
    addresses_error,
    fetchUserAddresses,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  } = useStore();

  const [mode, setMode] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  useEffect(() => {
    fetchUserAddresses();
  }, [fetchUserAddresses]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    try {
      await deleteAddress(id);
      toast.success('Address deleted successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to delete address');
    }
  };

  const handleEdit = (address) => {
    setEditTarget(address);
    setMode('edit');
  };

  const handleMakeDefault = async (id) => {
    try {
      await setDefaultAddress(id);
      toast.success('Default address updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update default address');
    }
  };

  const handleAddSubmit = async (form) => {
    try {
      await addAddress(form);
      toast.success('Address saved successfully!');
      setMode(null);
    } catch (err) {
      toast.error(err.message || 'Failed to add address');
    }
  };

  const handleEditSubmit = async (form) => {
    try {
      await updateAddress(editTarget.address_id ?? editTarget.id, form);
      toast.success('Address updated successfully!');
      setMode(null);
      setEditTarget(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update address');
    }
  };

  const editInitial = editTarget
    ? {
        name: editTarget.name || '',
        mobile: editTarget.mobile || editTarget.phone || '',
        email: editTarget.email || '',
        address_type: editTarget.address_type || editTarget.type || 'Home',
        address: editTarget.address || editTarget.street || '',
        pincode: editTarget.pincode || '',
        city: editTarget.city || '',
        state: editTarget.state || '',
        gstin: editTarget.gstin || editTarget.gst_number || '',
        latitude: editTarget.latitude ?? '',
        longitude: editTarget.longitude ?? '',
        is_default: editTarget.is_default || false,
      }
    : EMPTY_FORM;

  const defaultAddr = user_addresses.find((a) => a.is_default);
  const otherAddrs = user_addresses.filter((a) => !a.is_default);

  return (
    <div className="font-sans">
      {/* ── Header ── */}
      <div className="mb-5 sm:mb-6 flex items-center justify-between border-b border-gray-100 pb-3 sm:pb-4 gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-base sm:text-xl font-black text-gray-900">
              My Addresses
            </h1>
            {user_addresses.length > 0 && (
              <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-emerald-50 px-2 text-[10px] sm:text-[11px] font-bold text-[#046a38] border border-emerald-200">
                {user_addresses.length}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-gray-500 hidden sm:block">
            Manage your saved delivery locations for faster checkout.
          </p>
        </div>

        {/* Add New Address Button */}
        {user_addresses.length > 0 && (
          <button
            type="button"
            onClick={() => setMode('add')}
            className="flex items-center gap-1.5 rounded-xl bg-[#046a38] px-3.5 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm shadow-[#046a38]/20 hover:bg-[#03522b] active:scale-95 transition shrink-0"
          >
            <AddOutlinedIcon style={{ fontSize: 18 }} />
            <span>Add New</span>
          </button>
        )}
      </div>

      {/* ── Loading Spinner ── */}
      {addresses_loading && user_addresses.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-gray-200 border-t-[#046a38]" />
          <p className="text-xs text-gray-400 font-medium">Loading saved addresses…</p>
        </div>
      )}

      {/* ── Error Banner ── */}
      {addresses_error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3.5 text-xs font-semibold text-red-600">
          {addresses_error}
        </div>
      )}

      {/* ── Empty State ── */}
      {!addresses_loading && !addresses_error && user_addresses.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-emerald-50/20 py-12 sm:py-16 px-4 text-center">
          <div className="mb-3.5 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-[#046a38] shadow-xs">
            <LocationOnOutlinedIcon style={{ fontSize: 34 }} />
          </div>
          <h2 className="text-sm sm:text-base font-bold text-gray-900">
            No saved addresses yet
          </h2>
          <p className="mt-1 text-xs text-gray-500 max-w-xs leading-relaxed">
            Add your home or office address to enjoy seamless daily deliveries.
          </p>
          <button
            type="button"
            onClick={() => setMode('add')}
            className="mt-5 flex items-center gap-1.5 rounded-xl bg-[#046a38] px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm hover:bg-[#03522b] active:scale-95 transition"
          >
            <AddOutlinedIcon style={{ fontSize: 18 }} />
            <span>Add Delivery Address</span>
          </button>
        </div>
      )}

      {/* ── Default Address Section ── */}
      {defaultAddr && (
        <div className="mb-5 sm:mb-6">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#046a38]">
            <StarOutlinedIcon style={{ fontSize: 13 }} />
            <span>Default Delivery Location</span>
          </div>
          <AddressCard
            address={defaultAddr}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMakeDefault={handleMakeDefault}
          />
        </div>
      )}

      {/* ── Other Addresses Section ── */}
      {otherAddrs.length > 0 && (
        <div>
          <div className="mb-2.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
            <span>Other Saved Addresses ({otherAddrs.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            {otherAddrs.map((address) => (
              <AddressCard
                key={address.id || address.address_id}
                address={address}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMakeDefault={handleMakeDefault}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {mode === 'add' && (
        <AddressFormModal
          title="Add New Address"
          loading={addresses_loading}
          onSubmit={handleAddSubmit}
          onCancel={() => setMode(null)}
        />
      )}

      {mode === 'edit' && editTarget && (
        <AddressFormModal
          title="Edit Address"
          initial={editInitial}
          loading={addresses_loading}
          onSubmit={handleEditSubmit}
          onCancel={() => {
            setMode(null);
            setEditTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default AddressesSection;
